import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';

import { Modal } from '@/common';
import { Label, LABEL_TYPE, Spinner, ZeroState } from '@/components/generic';
import { PAGE_SIZE, TEXT } from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetType, PERMISSION_TYPE, SLOT_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getNumberOfAssetSlots } from '@/helpers';
import { AssetsContainer } from '@/pages/Event/Asset/AssetsContainer';
import { CreateEdit } from '@/pages/Event/Text/CreateEdit';
import { RecommendedSpeeches } from '@/pages/Event/Text/RecommendedSpeeches';
import { FileIcon as AddIcon } from '@/resources';
import { useGetEventAssets, useGetEventByIdQuery, useGetEventTags } from '@/services';
import { Asset, AssetSlot, Tag } from '@/types';

type TextSectionProps = {
  isUploaderPage?: boolean;
  organizationPermissions?: string[];
};

export const TextSection = ({
  isUploaderPage = false,
  organizationPermissions: eventOrganizationPermissions
}: TextSectionProps) => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();
  const [isCreateEditMode, setIsCreateEditMode] = useState<boolean>(false);
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);
  const { inView } = useInView();
  const [selectedRecommendedText, setSelectedRecommendedText] = useState<Asset>();
  const { data: eventData, isLoading: isEventLoading } = useGetEventByIdQuery(Number(eventId));
  const { isLoading: isLoadingTags, data: allTags } = useGetEventTags(eventId ?? '');
  const [selectedTextId, setSelectedTextId] = useState<number>(0);
  const { permissions, organizationPermissions: userOrganizationPermissions } =
    useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const numberOfSlots = eventData?.data?.assetSlots.filter(
    (slot: AssetSlot) => slot?.slotType?.id === SLOT_TYPE.TEXT
  ).length;

  const hasTextAssetCreationAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.TEXT_ASSETS_CREATION
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const handleWriteTextMode = (isCreateMode: boolean) => {
    setIsWriteTextMode(isCreateMode);
  };

  const handleIsEditPage = (value: boolean) => {
    setIsEditPage(value);
  };

  const {
    isLoading,
    data: allAssets,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useGetEventAssets(
    String(eventId),
    isUploaderPage
      ? allTags?.data
          ?.filter((tag: Tag) => [TEXT].includes(tag.name))
          .map((tag: Tag) => `${tag.id}`)
      : [],
    PAGE_SIZE,
    '',
    [AssetType.TEXT.toString()],
    undefined,
    undefined,
    hasContentManagementAccess
  );

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  const handleOpenCreateMode = (item: Asset) => {
    setSelectedRecommendedText(item);
    setIsCreateEditMode(true);
  };

  const handleCloseCreateEditMode = () => {
    setIsCreateEditMode(false);
    setSelectedRecommendedText(undefined);
    setSelectedTextId(0);
  };

  useEffect(() => {
    handleWriteTextMode?.(isCreateEditMode);
  }, [isCreateEditMode]);

  const groupedAssets = (
    allAssets?.pages?.flatMap((page) => page.data?.items)?.map((asset: Asset) => asset) || []
  ).length;

  const groupedAssetSlots = getNumberOfAssetSlots(eventData?.data.assetSlots, [TEXT]);

  const handleEditText = (asset: Asset) => {
    setIsCreateEditMode(true);
    setSelectedTextId(asset.id);
  };

  useEffect(() => {
    handleIsEditPage?.(!!selectedTextId);
  }, [selectedTextId]);

  const handleDisable = () => {
    return !!(isUploaderPage && groupedAssetSlots <= groupedAssets);
  };

  const [isWriteTextMode, setIsWriteTextMode] = useState<boolean>(false);
  const [isEditPage, setIsEditPage] = useState<boolean>(false);

  return (
    <div
      className={`flex size-full flex-col ${hasTextAssetCreationAccess && hasContentManagementAccess ? `${!(isLoading || isEventLoading || isLoadingTags) && 'no-scrollbar overflow-y-auto'}` : 'relative overflow-hidden'} ${
        hasOrgAccess ? 'w-full md:px-20' : 'max-w-[960px]'
      }`}>
      {((hasContentManagementAccess && isLoading) || isEventLoading || isLoadingTags) && (
        <Spinner />
      )}
      {(hasContentManagementAccess ? !isLoading : true) && !isEventLoading && !isLoadingTags && (
        <div className='flex size-full flex-col justify-start gap-y-4 px-1 sm:gap-y-7'>
          {!hasOrgAccess && (
            <div className='flex w-full sm:justify-end'>
              <div className='flex w-full flex-col gap-x-6 sm:mt-9 sm:items-end sm:justify-end'>
                <div
                  className={`flex w-full flex-row items-center justify-between pb-4 ${
                    !hasOrgAccess && 'mb-2 border-b border-secondary-blue-gray'
                  }`}>
                  <Label
                    text={
                      isWriteTextMode ? t(isEditPage ? 'editText' : 'writeText') : t('uploadedText')
                    }
                    type={LABEL_TYPE.H2}
                  />
                  <div className='z-10 inline-flex flex-col items-end gap-2.5 px-2'>
                    <div className='font-sans text-xl font-semibold text-primary-blue'>
                      {`${groupedAssets ?? 0}/${groupedAssetSlots}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {allAssets?.pages[0].data?.items.length === 0 ? (
            <ZeroState
              icon={<AddIcon />}
              titleFirstLine={t('addTextTitleOne')}
              titleSecondLine={t('addTextTitleTwo')}
              className={`${hasOrgAccess && 'mt-6'}`}
            />
          ) : (
            <div
              className={`h-full max-h-[23vh] rounded-md border border-secondary-blue-gray p-2.5 3xl:max-h-[28vh] 4xl:max-h-[33vh] ${hasOrgAccess ? 'mt-6 w-full' : 'max-w-[960px]'}`}>
              <AssetsContainer
                eventId={eventId ?? ''}
                justContent
                assetType={TEXT}
                isScrollable={false}
                className='!mt-0 !px-0'
                assetTag={
                  isUploaderPage ? allTags?.data?.filter((tag: Tag) => tag.name === TEXT) : []
                }
                canBeEdited
                editSelectedAsset={handleEditText}
                grow={false}
              />
            </div>
          )}
          <div className='flex w-full flex-1 flex-col gap-x-10 md:mb-5 md:flex-row'>
            <CreateEdit
              id={String(eventId)}
              recommendedAsset={selectedRecommendedText as Asset}
              handleClose={handleCloseCreateEditMode}
              isUploaderPage
              textId={selectedTextId}
              maxAssets={groupedAssetSlots}
              disabled={!!(isUploaderPage && groupedAssetSlots <= groupedAssets)}
            />
            <RecommendedSpeeches
              handleOpenCreateMode={handleOpenCreateMode}
              eventId={Number(eventId ?? 0)}
              isDisabledAddButton={isUploaderPage && handleDisable()}
              isUploaderPage
              numberOfAssets={groupedAssets}
              slotsNumber={numberOfSlots}
              refetchTextAssets={refetch}
            />
          </div>
        </div>
      )}
      {(!hasTextAssetCreationAccess || !hasContentManagementAccess) && (
        <div className='absolute inset-0 z-10 rounded-2xl backdrop-blur-sm'>
          <Modal
            isOpen={!hasTextAssetCreationAccess || !hasContentManagementAccess}
            modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
            hasCloseIcon={false}
            onClose={() => {
              undefined;
            }}>
            <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
              <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
              <Label
                type={LABEL_TYPE.PARAGRAPH2}
                text={t('yourOranizationDoesntHavePermission', {
                  permission: hasTextAssetCreationAccess
                    ? hasContentManagementAccess
                      ? ''
                      : t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`)
                    : t(`${ORGANIZATION_PERMISSION_TYPE.TEXT_ASSETS_CREATION}`)
                })}
              />
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};
